import { useState } from "react";
import { Reveal } from "@/components/Motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const money = (n) => "$" + Math.round(n).toLocaleString();
const Field = ({ label, ...p }) => (
  <div><Label>{label}</Label><Input type="number" {...p} className="bg-white" /></div>
);

function Mortgage() {
  const [v, setV] = useState({ price: 400000, down: 80000, rate: 6.5, term: 30, tax: 4000, ins: 1400, hoa: 0, pmi: 0 });
  const s = (k) => (e) => setV({ ...v, [k]: +e.target.value });
  const loan = Math.max(0, v.price - v.down);
  const r = v.rate / 100 / 12, n = v.term * 12;
  const pi = r > 0 ? (loan * r) / (1 - Math.pow(1 + r, -n)) : loan / n;
  const monthly = pi + v.tax / 12 + v.ins / 12 + v.hoa + v.pmi / 12;
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Purchase price" value={v.price} onChange={s("price")} data-testid="mort-price" />
        <Field label="Down payment" value={v.down} onChange={s("down")} data-testid="mort-down" />
        <Field label="Interest rate %" value={v.rate} step="0.1" onChange={s("rate")} data-testid="mort-rate" />
        <Field label="Loan term (yrs)" value={v.term} onChange={s("term")} data-testid="mort-term" />
        <Field label="Property taxes/yr" value={v.tax} onChange={s("tax")} />
        <Field label="Insurance/yr" value={v.ins} onChange={s("ins")} />
        <Field label="HOA/mo" value={v.hoa} onChange={s("hoa")} />
        <Field label="Mortgage insurance/yr" value={v.pmi} onChange={s("pmi")} />
      </div>
      <div className="bg-navy text-cream rounded-3xl p-8 flex flex-col justify-center">
        <div className="text-cream/60 text-sm">Estimated monthly payment</div>
        <div className="font-serif text-5xl text-gold my-3" data-testid="mort-result">{money(monthly)}</div>
        <div className="space-y-2 text-sm border-t border-white/10 pt-4">
          <Row label="Principal & interest" v={money(pi)} />
          <Row label="Taxes" v={money(v.tax / 12)} />
          <Row label="Insurance" v={money(v.ins / 12)} />
          <Row label="HOA" v={money(v.hoa)} />
          <Row label="Mortgage insurance" v={money(v.pmi / 12)} />
        </div>
        <p className="text-xs text-cream/50 mt-5">This calculator provides an estimate for educational purposes only. It is not a loan offer, approval, or guarantee. Actual payments, rates, taxes, insurance, and terms may vary.</p>
      </div>
    </div>
  );
}

function Affordability() {
  const [v, setV] = useState({ income: 90000, debts: 500, down: 40000, rate: 6.5, payment: 2200 });
  const s = (k) => (e) => setV({ ...v, [k]: +e.target.value });
  const maxPayment = Math.min(v.payment, (v.income / 12) * 0.36 - v.debts);
  const r = v.rate / 100 / 12, n = 360;
  const loan = r > 0 ? maxPayment * (1 - Math.pow(1 + r, -n)) / r : maxPayment * n;
  const low = Math.max(0, loan + v.down) * 0.9, high = Math.max(0, loan + v.down) * 1.05;
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Household income/yr" value={v.income} onChange={s("income")} data-testid="aff-income" />
        <Field label="Monthly debts" value={v.debts} onChange={s("debts")} data-testid="aff-debts" />
        <Field label="Down payment" value={v.down} onChange={s("down")} />
        <Field label="Interest rate %" value={v.rate} step="0.1" onChange={s("rate")} />
        <Field label="Desired monthly payment" value={v.payment} onChange={s("payment")} />
      </div>
      <div className="bg-navy text-cream rounded-3xl p-8 flex flex-col justify-center">
        <div className="text-cream/60 text-sm">Estimated Planning Range</div>
        <div className="font-serif text-4xl text-gold my-3" data-testid="aff-result">{money(low)} – {money(high)}</div>
        <p className="text-xs text-cream/50">This is a planning range for educational purposes — not a mortgage approval amount. Speak with a lender for an official preapproval.</p>
      </div>
    </div>
  );
}

function SellerNet() {
  const [v, setV] = useState({ price: 450000, payoff: 220000, repairs: 3000, concessions: 5000, closing: 9000, commission: 27000, other: 1000 });
  const s = (k) => (e) => setV({ ...v, [k]: +e.target.value });
  const deductions = v.payoff + v.repairs + v.concessions + v.closing + v.commission + v.other;
  const net = v.price - deductions;
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Expected sale price" value={v.price} onChange={s("price")} data-testid="net-price" />
        <Field label="Mortgage payoff" value={v.payoff} onChange={s("payoff")} data-testid="net-payoff" />
        <Field label="Estimated repairs" value={v.repairs} onChange={s("repairs")} />
        <Field label="Seller concessions" value={v.concessions} onChange={s("concessions")} />
        <Field label="Closing costs" value={v.closing} onChange={s("closing")} />
        <Field label="Brokerage compensation" value={v.commission} onChange={s("commission")} />
        <Field label="Other expenses" value={v.other} onChange={s("other")} />
      </div>
      <div className="bg-navy text-cream rounded-3xl p-8 flex flex-col justify-center">
        <Row label="Estimated gross proceeds" v={money(v.price)} />
        <Row label="Estimated deductions" v={"-" + money(deductions)} />
        <div className="border-t border-white/10 mt-4 pt-4">
          <div className="text-cream/60 text-sm">Estimated net proceeds</div>
          <div className="font-serif text-5xl text-gold my-2" data-testid="net-result">{money(net)}</div>
        </div>
        <p className="text-xs text-cream/50 mt-3">All calculations are estimates only.</p>
      </div>
    </div>
  );
}

const Row = ({ label, v }) => <div className="flex justify-between"><span className="text-cream/70">{label}</span><span className="font-mono">{v}</span></div>;

export default function Calculators() {
  return (
    <div className="bg-cream pt-28 pb-24 px-6 md:px-12 min-h-screen">
      <div className="max-w-5xl mx-auto">
        <Reveal><h1 className="font-serif text-5xl md:text-6xl text-navy tracking-tighter mb-3">Calculators</h1></Reveal>
        <Reveal delay={0.1}><p className="text-charcoal/70 mb-10 max-w-lg">Educational tools to help you plan. All results are estimates, not offers or approvals.</p></Reveal>
        <Tabs defaultValue="mortgage">
          <TabsList className="bg-white rounded-full p-1 mb-8">
            <TabsTrigger value="mortgage" data-testid="tab-mortgage" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-cream">Mortgage Payment</TabsTrigger>
            <TabsTrigger value="afford" data-testid="tab-afford" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-cream">Affordability</TabsTrigger>
            <TabsTrigger value="net" data-testid="tab-net" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-cream">Seller Net Proceeds</TabsTrigger>
          </TabsList>
          <TabsContent value="mortgage"><Mortgage /></TabsContent>
          <TabsContent value="afford"><Affordability /></TabsContent>
          <TabsContent value="net"><SellerNet /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
